import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { HydrationProvider } from "@/hooks/useHydration";

// Homepage loads eagerly — it's the entry point and must render immediately
import HomePage from "@/pages/HomePage";

// Lightweight loading fallback (no layout shift — minimal height, no CLS)
function RouteLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
      <div style={{ width: 24, height: 24, border: '3px solid #333', borderTop: '3px solid #FACC15', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Lazy-loaded routes — each gets its own chunk
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const BlogListPage = lazy(() => import("@/pages/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const LegacyGeneratorArticlePage = lazy(() => import("@/pages/LegacyGeneratorArticlePage"));
const LegacyCatchAll = lazy(() => import("@/pages/LegacyCatchAll"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const CATEGORY_SLUGS = new Set([
  'residential-electrical-services-nashville',
  'commercial-electrical-services-nashville',
]);

function ServiceRouter() {
  const { slug } = useParams();
  if (CATEGORY_SLUGS.has(slug)) return <ServicePage />;
  return <ServiceDetailPage />;
}

// Each lazy route gets its own Suspense boundary so the eagerly loaded
// HomePage hydrates without a Suspense ancestor (prerendered snapshot has no <!--$--> markers).
const lazyEl = (Component) => (
  <Suspense fallback={<RouteLoader />}>
    <Component />
  </Suspense>
);

function App() {
  return (
    <HydrationProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/admin" element={lazyEl(AdminPage)} />
            <Route path="/review/:token" element={lazyEl(ReviewPage)} />
            <Route path="/blog" element={lazyEl(BlogListPage)} />
            <Route path="/blog/:slug" element={lazyEl(BlogPostPage)} />
            <Route path="/services/:slug" element={lazyEl(ServiceRouter)} />
            <Route path="/book" element={lazyEl(BookingPage)} />
            <Route path="/contact" element={lazyEl(ContactPage)} />
            <Route path="/about" element={lazyEl(AboutPage)} />
            <Route path="/faq" element={lazyEl(FAQPage)} />
            <Route path="/privacy-policy" element={lazyEl(PrivacyPolicyPage)} />
            <Route path="/terms" element={lazyEl(TermsPage)} />
            <Route path="/2025/11/26/how-to-connect-a-portable-generator-to-your-home-main-panel" element={lazyEl(LegacyGeneratorArticlePage)} />
            <Route path="*" element={lazyEl(LegacyCatchAll)} />
          </Routes>
      </BrowserRouter>
    </div>
  </HydrationProvider>
  );
}

export default App;
